import React, { useCallback, useEffect, useState } from 'react';

import {
    cancel_customer_request,
    create_customer_request,
    fetch_customer_requests,
} from '../../api/route';
import { ActivityIndicator } from './ActivityIndicator';

const emptyManualAddress = () => ({
    name: '',
    address: '',
    postcode: '',
    additional_info: '',
});

const CustomerRouteRequests = ({ isDark, deliveries, showAlert }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [requestType, setRequestType] = useState('dedicated');
    const [deliveryId, setDeliveryId] = useState('');
    const [date, setDate] = useState('');
    const [pallets, setPallets] = useState('');
    const [weight, setWeight] = useState('');
    const [bookedTime, setBookedTime] = useState('');
    const [comments, setComments] = useState('');
    const [collection, setCollection] = useState(emptyManualAddress());
    const [delivery, setDelivery] = useState(emptyManualAddress());
    const [error, setError] = useState('');

    const panelStyle = {
        background: isDark ? '#171717' : '#fff',
        border: `1px solid ${isDark ? '#444' : '#ddd'}`,
        borderRadius: 8,
        padding: 18,
        marginBottom: 20,
    };
    const inputStyle = {
        width: '100%',
        padding: '9px 10px',
        marginTop: 4,
        marginBottom: 12,
        borderRadius: 5,
        border: `1px solid ${isDark ? '#555' : '#bbb'}`,
        background: isDark ? '#242424' : '#fff',
        color: isDark ? '#eee' : '#222',
    };

    const loadRequests = useCallback(async () => {
        setLoading(true);
        const result = await fetch_customer_requests();
        setLoading(false);
        if (result.status === 200) {
            setRequests(result.data || []);
        } else {
            setError(result.error || 'Unable to load route requests.');
        }
    }, []);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const updateAddress = (setter, field, value) => {
        setter((current) => ({ ...current, [field]: value }));
    };

    const resetForm = () => {
        setDeliveryId('');
        setDate('');
        setPallets('');
        setWeight('');
        setBookedTime('');
        setComments('');
        setCollection(emptyManualAddress());
        setDelivery(emptyManualAddress());
        setError('');
    };

    const submit = async (event) => {
        event.preventDefault();
        setError('');

        if (!date || (!pallets && !weight)) {
            setError('Choose a date and enter at least pallets or weight.');
            return;
        }
        if (requestType === 'dedicated' && !deliveryId) {
            setError('Select one of your dedicated routes.');
            return;
        }
        if (requestType === 'manual') {
            const complete = [collection.name, collection.address, collection.postcode,
                delivery.name, delivery.address, delivery.postcode].every((value) => value.trim());
            if (!complete) {
                setError('Complete collection and delivery details are required.');
                return;
            }
        }

        const payload = {
            request_type: requestType,
            customer_deliveries_id: requestType === 'dedicated' ? Number(deliveryId) : undefined,
            date,
            pallets: pallets === '' ? null : Number(pallets),
            weight: weight === '' ? null : Number(weight),
            booked_time: bookedTime || null,
            comments: comments.trim() || null,
        };
        if (requestType === 'manual') {
            Object.assign(payload, {
                collection_name: collection.name.trim(),
                collection_address: collection.address.trim(),
                collection_postcode: collection.postcode.trim(),
                collection_additional_info: collection.additional_info.trim() || null,
                delivery_name: delivery.name.trim(),
                delivery_address: delivery.address.trim(),
                delivery_postcode: delivery.postcode.trim(),
                delivery_additional_info: delivery.additional_info.trim() || null,
            });
        }

        setSubmitting(true);
        const result = await create_customer_request(payload);
        setSubmitting(false);
        if (result.status === 201) {
            setRequests((current) => [result.data, ...current]);
            resetForm();
            showAlert({
                message: 'Your route request has been submitted for review.',
                type: 'success',
                buttons: [{ type: 'success', label: 'Close' }],
            });
        } else {
            setError(result.error || 'Unable to submit route request.');
        }
    };

    const cancel = async (requestId) => {
        const result = await cancel_customer_request(requestId);
        if (result.status === 200) {
            setRequests((current) => current.map((item) => item.id === requestId ? result.data : item));
        } else {
            showAlert({
                message: result.error || 'Unable to cancel route request.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    const renderAddress = (label, value, setter) => (
        <fieldset style={{ border: `1px solid ${isDark ? '#444' : '#ddd'}`, borderRadius: 6, padding: 12 }}>
            <legend style={{ fontSize: '1rem', fontWeight: 700, width: 'auto', padding: '0 6px' }}>{label}</legend>
            {['name', 'address', 'postcode'].map((field) => (
                <label key={field} style={{ display: 'block' }}>
                    {field === 'name' ? 'Company / Name' : field.charAt(0).toUpperCase() + field.slice(1)} *
                    <input
                        style={inputStyle}
                        value={value[field]}
                        onChange={(event) => updateAddress(setter, field, event.target.value)}
                    />
                </label>
            ))}
            <label style={{ display: 'block' }}>
                Additional information
                <textarea
                    style={inputStyle}
                    rows={2}
                    value={value.additional_info}
                    onChange={(event) => updateAddress(setter, 'additional_info', event.target.value)}
                />
            </label>
        </fieldset>
    );

    return (
        <div style={{ color: isDark ? '#eee' : '#222' }}>
            <h2 style={{ marginBottom: 16 }}>Request a Route</h2>
            <form onSubmit={submit} style={panelStyle}>
                <div style={{ display: 'flex', gap: 18, marginBottom: 14, flexWrap: 'wrap' }}>
                    <label>
                        <input type="radio" checked={requestType === 'dedicated'} onChange={() => setRequestType('dedicated')} />
                        {' '}Use a dedicated route
                    </label>
                    <label>
                        <input type="radio" checked={requestType === 'manual'} onChange={() => setRequestType('manual')} />
                        {' '}Enter collection and delivery locations
                    </label>
                </div>

                {requestType === 'dedicated' ? (
                    <label style={{ display: 'block' }}>
                        Dedicated route *
                        <select style={inputStyle} value={deliveryId} onChange={(event) => setDeliveryId(event.target.value)}>
                            <option value="">Select a route</option>
                            {deliveries.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.route_name || item.company_name} — {item.company_name}, {item.postcode}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 14 }}>
                        {renderAddress('Collection', collection, setCollection)}
                        {renderAddress('Delivery', delivery, setDelivery)}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    <label>Date *<input type="date" style={inputStyle} value={date} onChange={(event) => setDate(event.target.value)} /></label>
                    <label>Pallets<input type="number" min="0" step="1" style={inputStyle} value={pallets} onChange={(event) => setPallets(event.target.value)} /></label>
                    <label>Weight (kg)<input type="number" min="0" step="1" style={inputStyle} value={weight} onChange={(event) => setWeight(event.target.value)} /></label>
                    <label>Requested booking time<input type="time" style={inputStyle} value={bookedTime} onChange={(event) => setBookedTime(event.target.value)} /></label>
                </div>
                <label style={{ display: 'block' }}>
                    Comments
                    <textarea style={inputStyle} rows={3} maxLength={1000} value={comments} onChange={(event) => setComments(event.target.value)} />
                </label>
                {error && <div style={{ color: '#dc3545', marginBottom: 12 }}>{error}</div>}
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Route Request'}
                </button>
            </form>

            <div style={panelStyle}>
                <h3 style={{ marginBottom: 14 }}>My Requests</h3>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><ActivityIndicator size={35} /></div>
                ) : requests.length === 0 ? (
                    <div style={{ opacity: 0.7 }}>You have not submitted any route requests.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>{['Date', 'Route', 'Collection', 'Delivery', 'Load', 'Status', 'Reason', ''].map((heading) => <th key={heading} style={{ padding: 9, textAlign: 'left', borderBottom: `2px solid ${isDark ? '#555' : '#ccc'}` }}>{heading}</th>)}</tr>
                            </thead>
                            <tbody>
                                {requests.map((item) => (
                                    <tr key={item.id}>
                                        <td style={{ padding: 9 }}>{item.date}</td>
                                        <td style={{ padding: 9 }}>{item.route || 'Manual request'}</td>
                                        <td style={{ padding: 9 }}>{item.collection_name}<br /><small>{item.collection_postcode}</small></td>
                                        <td style={{ padding: 9 }}>{item.delivery_name}<br /><small>{item.delivery_postcode}</small></td>
                                        <td style={{ padding: 9 }}>{[item.pallets != null ? `${item.pallets} pallets` : null, item.weight != null ? `${item.weight} kg` : null].filter(Boolean).join(' / ')}</td>
                                        <td style={{ padding: 9, textTransform: 'capitalize' }}>{item.status}</td>
                                        <td style={{ padding: 9 }}>{item.decline_reason || '—'}</td>
                                        <td style={{ padding: 9 }}>{item.status === 'pending' && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => cancel(item.id)}>Cancel</button>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerRouteRequests;
